import { useState, useRef, useMemo, useEffect, useCallback, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";

import useAppStore from "../store/useAppStore";
import useLayoutStore from "../store/useLayoutStore";
import useAuthStore from "../store/useAuthStore";

import ProfileModal from "./elements/ProfileModal";
import ChangeShiftStatusModal from "./elements/ChangeShiftStatusModal";
import NotificationModal from "./elements/NotificationModal";
import ShiftConfirmationModal from "./elements/ShiftConfirmationModal";

const Sidebar = () => {
    const setActiveSection = useAppStore(state => state.setActiveSection);
    const isExpanded = useAppStore(state => state.isSidebarExpanded)
    const workCheckInToast = useAppStore(state => state.workCheckInToast);
    const selectedShiftConfirmationNotificationId = useAppStore(state => state.selectedShiftConfirmationNotificationId);

    const smallMediumScreen = useLayoutStore(state => state.smallMediumScreen);

    const isDarkTheme = useLayoutStore(state => state.isDarkTheme)

    const userRole = useAuthStore(state => state.userRole);
    const isAdmin = userRole === 'admin';
    const landingUrl = import.meta.env.VITE_LANDING_URL;
    const sidebarRef = useRef(null);

    const handleButtonsClick = (e) => {
        const button = e.target.closest('button[data-name]');
        if (!button) return
        const name = button.getAttribute('data-name');
        setActiveSection(name);
    };

    const sidebarWidth = smallMediumScreen ? 59 : 84;
    const buttonWidth = smallMediumScreen ? 49 : 50;

    // Filtriranje buttons na osnovu userRole
    const visibleButtons = useMemo(() => {
        return buttons.filter(btn => !btn.adminOnly || isAdmin);
    }, [isAdmin]);


    return (
        <motion.div
            ref={sidebarRef}
            className={`sidebar-container ${isDarkTheme ? 'sb-dark-1' : 'sb-white-1'}`}
            style={{ willChange: 'width' }}
            animate={{ width: isExpanded ? 300 : sidebarWidth }}
            transition={{ duration: isExpanded ? 0 : 0.14, type: "tween" }}
        >
            <div className="sidebar-top">
                <HamburgerMenuButton />
                <motion.button
                    className="sidebar-portir-btn"
                    animate={{ width: isExpanded ? 240 : buttonWidth }}
                    transition={{ duration: 0.08, ease: "easeOut", delay: isExpanded ? 0.04 : 0 }}
                    style={{ willChange: 'width' }}
                    // style={{ transform: `translateX(${isExpanded ? '0' : '1px'})` }}
                    onClick={() => window.location.assign(landingUrl)}
                >
                    <img src={isDarkTheme ? "./sidebar/PortirMunjaBela.svg" : "./sidebar/NormMunjica.svg"}
                        alt="Portir Logo" className="sidebar-portir-logo" />
                    <AnimatePresence>
                        {isExpanded && (
                            <motion.img
                                className="portir-logo-text-img"
                                // src="./sidebar/portirLogoText.svg"
                                src={isDarkTheme ? "./sidebar/PortirLogoTextBeo.svg" : "./sidebar/PortirLogoText.svg"}
                                initial={{ scaleX: 0 }}
                                animate={{ scaleX: 1 }}
                                transition={{ duration: 0.04, ease: "easeOut", delay: 0.04 }}
                                exit={{ scaleX: 0, scaleY: 0, opacity: 0, transition: { duration: 0.14, ease: "easeOut", delay: 0.04 } }}
                            >
                            </motion.img>
                        )}
                    </AnimatePresence>
                </motion.button>
                {/* <motion.div className="sb-search-container">
                    <motion.div
                        className="sidebar-search-icon-div"
                        animate={{ x: isExpanded ? 100 : 0, scale: isExpanded ? 0.84 : 1 }}
                        transition={{ duration: 0.08, ease: "easeOut", delay: 0.1 }}
                    >
                        <img
                            src="./sidebar/Pretrazivanje.svg"
                            alt="Search Icon"
                            // className="sidebar-search-icon" 
                            className={`sidebar-search-icon ${isDarkTheme ? 'dark-filter' : ''}`}
                        />
                    </motion.div>
                    <AnimatePresence>
                        {isExpanded && (
                            <motion.input
                                className={`sidebar-search-input ${isDarkTheme ? 'dark-sb-input' : ''}`}
                                placeholder="Pretraži..."
                                name="search"
                                initial={{ scaleX: 0, opacity: 0.1 }}
                                animate={{ scaleX: 1, opacity: 1 }}
                                transition={{ duration: 0.08, ease: "easeOut", delay: 0.14 }}
                                exit={{ scaleX: 0, opacity: 0, duration: 0.12 }}
                            />
                        )}
                    </AnimatePresence>
                </motion.div> */}

            </div>
            <div className="sidebar-divider"></div>
            <div className="sidebar-bottom" onClick={handleButtonsClick}>
                {visibleButtons.map((btn, index) => (
                    <SidebarButton key={btn.name} {...btn} index={index} width={buttonWidth} />
                ))}
            </div>
            <div className="sidebar-bottom-menu-container">
                <NotificationButton width={buttonWidth} />
                <ProfileButton width={buttonWidth} />
            </div>
            <AnimatePresence>
                {workCheckInToast && <WorkCheckInToast key="work-checkin-toast" toastData={workCheckInToast} />}
            </AnimatePresence>
            <AnimatePresence>
                {selectedShiftConfirmationNotificationId && <ShiftConfirmationModal key="shift-confirmation-modal" />}
            </AnimatePresence>

        </motion.div>
    );
}

export default Sidebar;

const NotificationButton = ({ width }) => {
    const isDarkTheme = useLayoutStore(state => state.isDarkTheme)
    const loading = useAppStore(state => state.loading)
    const closeSidebar = useAppStore(state => state.closeSidebar);
    const isExpanded = useAppStore(state => state.isSidebarExpanded)
    const unreadNotificationCount = useAppStore(state => state.unreadNotificationCount)
    const isNotificationModalOpen = useAppStore(state => state.isNotificationModalOpen)
    const setIsNotificationModalOpen = useAppStore(state => state.setIsNotificationModalOpen)

    const isTouchDevice = useLayoutStore(state => state.isTouchDevice);

    const [isHovered, setIsHovered] = useState(false)

    const handleClick = () => {
        if (loading) return;
        closeSidebar(false);
        setIsNotificationModalOpen(true);
    };

    return (
        <div
            className="sidebar-btn-wrapper"

        >
            <motion.button
                className="notification-button sidebar-bottom-button"
                animate={{ width: isExpanded ? 240 : width }}
                transition={{ duration: 0.08, ease: "easeOut", delay: isExpanded ? 0.24 : 0 }}
                onClick={handleClick}
                style={{ position: 'relative', backgroundColor: 'transparent', willChange: 'width' }}
                onMouseEnter={() => !isTouchDevice && setIsHovered(true)}
                onMouseLeave={() => !isTouchDevice && setIsHovered(false)}
            >
                <div style={{ position: 'relative' }}>
                    <img
                        className={`notification-icon ${isDarkTheme ? 'dark-filter' : ''}`}
                        src="./cards/Zvonce.svg"
                        alt="Notification Icon"
                    />
                    <AnimatePresence mode="wait">
                        {unreadNotificationCount > 0 && (
                            <motion.div
                                key={unreadNotificationCount}
                                className="notification-badge"
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                exit={{ scale: 0 }}
                                transition={{ duration: 0.14, ease: "easeOut" }}
                            >
                                {unreadNotificationCount > 9 ? '?' : unreadNotificationCount}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
                <AnimatePresence>
                    {isExpanded && (
                        <motion.span
                            className="sidebar-btn-text"
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ duration: 0.14, ease: "easeOut", delay: isExpanded ? 0.24 : 0 }}
                            exit={{ scale: 0, transition: { duration: 0.14, ease: "easeOut", delay: 0.04 } }}
                        >
                            Notifikacije
                        </motion.span>
                    )}
                </AnimatePresence>
            </motion.button>
            <AnimatePresence>
                {!isExpanded && isHovered && (
                    <motion.div
                        className={`sidebar-tooltip ${isDarkTheme ? 'tooltip-dark' : 'tooltip-white'}`}
                        initial={{ opacity: 0, x: -14 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -14 }}
                        transition={{ duration: 0.19, ease: "easeOut" }}
                    >
                        Notifikacije
                    </motion.div>
                )}
            </AnimatePresence>
            <AnimatePresence>
                {isNotificationModalOpen && (
                    <NotificationModal
                        key="notification-modal"
                        onClose={() => setIsNotificationModalOpen(false)}
                    />
                )}
            </AnimatePresence>
        </div>
    )
}

const ProfileButton = () => {
    const isDarkTheme = useLayoutStore(state => state.isDarkTheme)
    const loading = useAppStore(state => state.loading)
    const isExpanded = useAppStore(state => state.isSidebarExpanded)
    const userId = useAuthStore(state => state.userId)
    const userImage = useAppStore(state => state.employeesData?.[userId]?.image)

    const closeSidebar = useAppStore(state => state.closeSidebar);
    const isWorkPresenceFormOpen = useAppStore(state => state.isWorkPresenceFormOpen);

    const isTouchDevice = useLayoutStore(state => state.isTouchDevice);

    const smallMediumScreen = useLayoutStore(state => state.smallMediumScreen);

    const [isHovered, setIsHovered] = useState(false)

    const buttonSize = smallMediumScreen ? 39 : 44

    const [isModalOpen, setIsModalOpen] = useState(false);

    const handleClick = () => {
        if (loading) return;
        closeSidebar(false);
        setIsModalOpen(true);
    };

    const profileImg = useMemo(() => {
        if (loading || !userImage) return null
        const baseImgUrl = import.meta.env.VITE_API_IMAGES_URL;
        return `${baseImgUrl}/employees/${userImage}`
    }, [loading, userImage])

    return (
        <div
            className="sidebar-btn-wrapper"
        >
            <motion.button
                className="profile-button sidebar-bottom-button"
                // animate={{ x: isExpanded ? 94 : 0 }}
                animate={{ width: isExpanded ? 240 : 44 }}
                transition={{ duration: 0.08, ease: "easeOut", delay: isExpanded ? 0.28 : 0 }}
                style={{ willChange: 'width' }}
                onClick={handleClick}
                onMouseEnter={() => !isTouchDevice && setIsHovered(true)}
                onMouseLeave={() => !isTouchDevice && setIsHovered(false)}
            >
                <motion.img
                    key={profileImg || "profile-fallback"}
                    src={profileImg || "/views/login.svg"}
                    crossOrigin="use-credentials"
                    className="profile-button-image"
                    animate={{ x: isExpanded ? 4 : 0 }}
                    transition={{ duration: 0.08, ease: "easeOut", delay: isExpanded ? 0.28 : 0 }}
                    style={{ willChange: 'transform' }}
                    onError={(e) => {
                        e.target.onerror = null; // sprečava loop
                        e.target.classList.add("profile-button-image-error"); // doda klasu
                        e.target.src = "/views/login.svg"; // fallback slika
                    }}
                />
                <AnimatePresence>
                    {isExpanded && (
                        <motion.span
                            className="sidebar-btn-text"
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ duration: 0.14, ease: "easeOut", delay: isExpanded ? 0.28 : 0 }}
                            exit={{ scale: 0, transition: { duration: 0.14, ease: "easeOut", delay: 0 } }}
                        >
                            Profil
                        </motion.span>
                    )}
                </AnimatePresence>
            </motion.button>
            <AnimatePresence>
                {!isExpanded && isHovered && (
                    <motion.div
                        className={`sidebar-tooltip ${isDarkTheme ? 'tooltip-dark' : 'tooltip-white'}`}
                        initial={{ opacity: 0.1, x: -14 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0.1, x: -14 }}
                        transition={{ duration: 0.19, ease: "easeOut" }}
                    >
                        Profil
                    </motion.div>
                )}
            </AnimatePresence>
            <AnimatePresence>
                {isModalOpen &&
                    <ProfileModal
                        key="profile-modal"
                        onClose={() => setIsModalOpen(false)}
                    />
                }
            </AnimatePresence>
            <AnimatePresence>
                {isWorkPresenceFormOpen &&
                    <ChangeShiftStatusModal
                        key="change-shift-status-modal"
                    />
                }
            </AnimatePresence>
        </div>
    )
}

const WorkCheckInToast = ({ toastData }) => {
    const isDarkTheme = useLayoutStore(state => state.isDarkTheme);
    const setWorkCheckInToast = useAppStore(state => state.setWorkCheckInToast);

    const dismiss = useCallback(() => {
        setWorkCheckInToast(null);
    }, [setWorkCheckInToast]);

    useEffect(() => {
        const timer = setTimeout(dismiss, 50000);
        return () => clearTimeout(timer);
    }, [dismiss]);

    const content = (
        <motion.div
            className="dv-device-toast-container"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={dismiss}
            style={{ pointerEvents: "auto", cursor: "pointer" }}
        >
            <motion.div
                className={`dv-device-toast-message ${isDarkTheme ? "dark-dv-device-toast-message" : "white-dv-device-toast-message"}`}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.2 }}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="na-toast-content">
                    <button
                        type="button"
                        className="na-toast-close-button"
                        onClick={(e) => {
                            e.stopPropagation();
                            dismiss();
                        }}
                    >
                        <svg width="20" height="20" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <line x1="6.8" y1="6.8" x2="21.2" y2="21.2" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                            <line x1="21.2" y1="6.8" x2="6.8" y2="21.2" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                        </svg>
                    </button>
                    <h4 className="na-toast-title">{toastData?.title || "Obaveštenje"}</h4>
                    <p className="na-toast-message-text">
                        {toastData?.message || "Status evidencije je ažuriran."}
                    </p>
                </div>
            </motion.div>
        </motion.div>
    );

    return createPortal(content, document.body);
};


const SidebarButton = ({ name, icon, alt, index, width }) => {
    const activeSection = useAppStore(state => state.activeSection);
    const isExpanded = useAppStore(state => state.isSidebarExpanded)

    const isTouchDevice = useLayoutStore(state => state.isTouchDevice);
    const isDarkTheme = useLayoutStore(state => state.isDarkTheme)

    const activeClass = isDarkTheme ? 'dark-active' : 'white-active'

    const [isHovered, setIsHovered] = useState(false)

    return (
        <div
            className="sidebar-btn-wrapper"
            onMouseEnter={() => !isTouchDevice && setIsHovered(true)}
            onMouseLeave={() => !isTouchDevice && setIsHovered(false)}
        >
            <motion.button
                key={name}
                data-name={name}
                className={activeSection === name ? activeClass : ""}
                animate={{ width: isExpanded ? 240 : width }}
                transition={{ duration: 0.08, ease: "easeOut", delay: index * 0.04 }}
                style={{ willChange: 'width, height' }}
            >
                {/* <img src={icon} alt={alt} className="sidebar-icon" /> */}
                <img src={icon} alt={alt} className={`sidebar-icon ${isDarkTheme ? 'dark-filter' : ''}`} />

                <AnimatePresence>
                    {isExpanded && (
                        <motion.span
                            className="sidebar-btn-text"
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ duration: 0.14, ease: "easeOut", delay: index * 0.04 }}
                            exit={{ scale: 0 }}
                        >
                            {name}
                        </motion.span>
                    )}
                </AnimatePresence>
            </motion.button>
            <AnimatePresence>
                {!isExpanded && isHovered && (
                    <motion.div
                        // className="sidebar-tooltip"
                        className={`sidebar-tooltip ${isDarkTheme ? 'tooltip-dark' : 'tooltip-white'}`}
                        initial={{ opacity: 0.1, x: -14 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0.1, x: -14 }}
                        transition={{ duration: 0.19, ease: "easeOut" }}
                    >
                        {name}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};


const HamburgerMenuButton = () => {
    const isExpanded = useAppStore(state => state.isSidebarExpanded);
    const toggleSidebar = useAppStore(state => state.toggleSidebar);

    const isDarkTheme = useLayoutStore(state => state.isDarkTheme)

    return (
        <motion.div
            className="hamburger-menu-button-container"
            animate={{
                x: isExpanded ? 114 : 0
            }}
            transition={{
                duration: 0.16,
                ease: "easeOut",
                delay: isExpanded ? 0.2 : 0.14
            }}
            style={{ willChange: 'transform' }}
        >
            <div className="hamburger-menu-button" onClick={toggleSidebar}>
                <motion.span
                    className={`hamBar hb1 ${isDarkTheme ? 'bg-dark' : 'bg-white'}`}
                    animate={isExpanded ? { rotate: 45, y: 10.5 } : { rotate: 0, y: 0 }}
                    transition={{
                        type: "spring",
                        stiffness: 300,
                        damping: 20,
                        delay: isExpanded ? 0 : 0.1
                    }}
                />
                <motion.span
                    className={`hamBar hb2 ${isDarkTheme ? 'bg-dark' : 'bg-white'}`}
                    animate={isExpanded ? { rotate: -45 } : { rotate: 0 }}
                    transition={{ duration: 0.2, delay: 0.1 }}
                />
                <motion.span
                    className={`hamBar hb3 ${isDarkTheme ? 'bg-dark' : 'bg-white'}`}
                    animate={isExpanded ? { rotate: -45, y: -10.5 } : { rotate: 0, y: 0 }}
                    transition={{
                        type: "spring",
                        stiffness: 300,
                        damping: 20,
                        delay: isExpanded ? 0.04 : 0
                    }}
                />
            </div>
        </motion.div>
    )
}

const buttons = [
    {
        name: "Prisutnost",
        icon: "./sidebar/Prisutnost.svg",
        alt: "Attendance Icon",
        adminOnly: false,
    },
    {
        name: "Statistika",
        icon: "./sidebar/Statistika.svg",
        alt: "Statistics Icon",
        adminOnly: false,
    },
    {
        name: "Karnet-obrazac",
        icon: "./sidebar/Karnet.svg",
        alt: "Report Card Icon",
        adminOnly: true,
    },
    {
        name: "Kalendar",
        icon: "./sidebar/Kalendar.svg",
        alt: "Calendar Icon",
        adminOnly: false,
    },
    {
        name: "Status uređaja",
        icon: "./sidebar/Uredjaji.svg",
        alt: "Devices Icon",
        adminOnly: false,
    },
    {
        name: "Podešavanja",
        icon: "./sidebar/Podesavanja.svg",
        alt: "Settings Icon",
        adminOnly: true,
    },
];